import type { PanelLayoutTransition } from '@affine/component/resize-panel';
import { Entity, LiveData } from '@toeverything/infra';
import type { Location, To } from 'history';
import { isEqual } from 'lodash-es';
import type { ParseOptions } from 'query-string';
import queryString from 'query-string';
import type { ReactNode } from 'react';
import { Observable } from 'rxjs';

import { createNavigableHistory } from '../../../utils/navigable-history';
import type { ViewIconName } from '../constants';
import { ViewScope } from '../scopes/view';
import { SidebarTab } from './sidebar-tab';

function omitUndefinedQueryValues(record: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(record).filter(([, value]) => value !== undefined)
  );
}

export interface ViewEndCover {
  width: number;
  dragged: boolean;
}

export class View extends Entity<{
  id: string;
  defaultLocation?: To | undefined;
  title?: string;
  icon?: ViewIconName;
}> {
  scope = this.framework.createScope(ViewScope, {
    view: this as View,
  });

  get id() {
    return this.props.id;
  }

  set id(id: string) {
    this.props.id = id;
  }

  sidebarTabs$ = new LiveData<SidebarTab[]>([]);

  /**
   * How the view takes a sidebar opening or closing beside it: laid out on every frame of the
   * slide, or once (`settled`) when each new width is costly for its content, as on a canvas.
   */
  layoutTransition$ = new LiveData<PanelLayoutTransition>('continuous');

  /** Set by a view that draws by its size, e.g. a canvas; see `holdSize`. */
  private sizeHold: ((room: number) => () => void) | null = null;

  /** Registers how the view keeps the size it draws at; returns the unregistration. */
  provideSizeHold(hold: (room: number) => () => void) {
    this.sizeHold = hold;
    return () => {
      if (this.sizeHold === hold) this.sizeHold = null;
    };
  }

  /**
   * The view's room is about to be slid frame by frame. Until the returned function is called the
   * view keeps the size it draws at and draws once, not on every frame. `room` is the width, in
   * px, the slide adds. A view that lays out cheaply holds nothing.
   */
  holdSize(room = 0): () => void {
    return this.sizeHold?.(room) ?? (() => {});
  }

  /** The view's mounted container (the workbench's split-view pane); null while it is not mounted. */
  element$ = new LiveData<HTMLElement | null>(null);

  /**
   * The view's content: its body without the room the workspace sidebar takes beside it. Null while
   * it is not mounted. What floats over the view's content (the AI island's row) positions on it.
   */
  contentElement$ = new LiveData<HTMLElement | null>(null);

  /**
   * The content's main pane when the content lays a navigation or list pane before it (two- and
   * three-pane layouts); null while the whole content is the main pane. What floats over the view's
   * content (Copilot's panel, the AI island's row) takes the main pane's room.
   */
  mainPane$ = new LiveData<HTMLElement | null>(null);
  private readonly mainPanes = new Set<HTMLElement>();

  /** A pane is the main pane while it is mounted; of nested ones the innermost is. */
  registerMainPane(pane: HTMLElement): () => void {
    this.mainPanes.add(pane);
    this.publishMainPane();
    return () => {
      this.mainPanes.delete(pane);
      this.publishMainPane();
    };
  }

  private publishMainPane() {
    const panes = [...this.mainPanes];
    this.mainPane$.next(
      panes.find(pane =>
        panes.every(other => other === pane || !pane.contains(other))
      ) ?? null
    );
  }

  /**
   * What the workbench's sidebar lies over of the view's end (over a canvas): its width in px, 0 while
   * it stands beside the view or is closed, and whether its edge is being dragged.
   */
  endCover$ = new LiveData<ViewEndCover>({ width: 0, dragged: false });

  // _activeTabId may point to a non-existent tab.
  // In this case, we still retain the activeTabId data and wait for the non-existent tab to be mounted.
  // sl-67k: Initialize from localStorage for persistence across sessions
  private static readonly SIDEBAR_TAB_STORAGE_KEY =
    'affine:sidebar:activeTabId';
  _activeSidebarTabId$ = new LiveData<string | null>(
    typeof window !== 'undefined'
      ? localStorage.getItem(View.SIDEBAR_TAB_STORAGE_KEY)
      : null
  );
  activeSidebarTab$ = LiveData.computed(get => {
    const activeTabId = get(this._activeSidebarTabId$);
    const tabs = get(this.sidebarTabs$);
    return tabs.length > 0
      ? (tabs.find(tab => tab.id === activeTabId) ?? tabs[0])
      : null;
  });

  constructor() {
    super();
    this.history = createNavigableHistory({
      initialEntries: [this.props.defaultLocation ?? { pathname: '/all' }],
      initialIndex: 0,
    });
  }

  history = createNavigableHistory({
    initialEntries: ['/all'],
    initialIndex: 0,
  });

  location$ = LiveData.from<Location>(
    new Observable(subscriber => {
      subscriber.next(this.history.location);
      return this.history.listen(update => {
        subscriber.next(update.location);
      });
    }),
    this.history.location
  );

  entries$ = LiveData.from<Location[]>(
    new Observable(subscriber => {
      subscriber.next(this.history.entries);
      return this.history.listen(() => {
        subscriber.next(this.history.entries);
      });
    }),
    this.history.entries
  );

  size$ = new LiveData(100);

  title$ = new LiveData(this.props.title ?? '');

  icon$ = new LiveData(this.props.icon ?? 'allDocs');

  queryString$<T extends Record<string, unknown>>(
    options: ParseOptions = {
      parseNumbers: true,
      parseBooleans: true,
    }
  ) {
    return this.location$
      .selector(v => v.search)
      .map(search => queryString.parse(search, options) as Partial<T>);
  }

  updateQueryString<T extends Record<string, unknown>>(
    patch: Partial<T>,
    {
      forceUpdate,
      parseNumbers,
      replace,
    }: {
      forceUpdate?: boolean;
      parseNumbers?: boolean;
      replace?: boolean;
    } = {}
  ) {
    const oldQueryStrings = queryString.parse(this.history.location.search, {
      parseBooleans: true,
      parseNumbers: parseNumbers,
    });
    const normalizedOldQueryStrings = omitUndefinedQueryValues(oldQueryStrings);
    const newQueryStrings = omitUndefinedQueryValues({
      ...oldQueryStrings,
      ...patch,
    });

    if (forceUpdate || !isEqual(normalizedOldQueryStrings, newQueryStrings)) {
      const query = queryString.stringify(newQueryStrings);

      // A location's search starts with "?" like the browser's, so pathname + search is its route.
      const newState = {
        ...this.history.location,
        search: query ? `?${query}` : '',
      };

      if (replace) {
        this.history.replace(newState);
      } else {
        this.history.push(newState);
      }
    }
  }

  push(path: To) {
    this.history.push(path);
  }

  go(n: number) {
    this.history.go(n);
  }

  replace(path: To) {
    this.history.replace(path);
  }

  setSize(size?: number) {
    this.size$.next(size ?? 100);
  }

  addSidebarTab(id: string, label?: string, icon?: ReactNode) {
    const existingTab = this.sidebarTabs$.value.find(tab => tab.id === id);
    if (existingTab) {
      if (existingTab.label === label) {
        return id;
      }
      this.sidebarTabs$.next(
        this.sidebarTabs$.value.map(tab =>
          tab.id === id
            ? this.scope.createEntity(SidebarTab, {
                id,
                label,
                icon,
              })
            : tab
        )
      );
      return id;
    }

    this.sidebarTabs$.next([
      ...this.sidebarTabs$.value,
      this.scope.createEntity(SidebarTab, {
        id,
        label,
        icon,
      }),
    ]);
    return id;
  }

  removeSidebarTab(id: string) {
    this.sidebarTabs$.next(
      this.sidebarTabs$.value.filter(tab => tab.id !== id)
    );
  }

  activeSidebarTab(id: string | null) {
    this._activeSidebarTabId$.next(id);
    // sl-67k: Persist sidebar tab selection to localStorage
    if (typeof window !== 'undefined') {
      if (id) {
        localStorage.setItem(View.SIDEBAR_TAB_STORAGE_KEY, id);
      } else {
        localStorage.removeItem(View.SIDEBAR_TAB_STORAGE_KEY);
      }
    }
  }

  setTitle(title: string) {
    this.title$.next(title);
  }

  setIcon(icon: ViewIconName) {
    this.icon$.next(icon);
  }
}
